/**
 * CertVerifyPage.jsx
 * PilotLinkUSA — Public Certificate Verification
 * Route: /verify/:certId  OR  /ppca/verify/:certId
 *
 * NO LOGIN REQUIRED — any company or dispatcher can verify
 *
 * Usage in React Router:
 *   <Route path="/verify/:certId" element={<CertVerifyPage />} />
 *
 * Django API endpoint needed:
 *   GET /api/ppca/verify/:certId/
 *   → returns cert data (see API_SCHEMA below)
 *
 * Dev toggle for PDF download:
 *   Set VITE_PPCA_DOWNLOAD=false in .env to disable download button
 *   Default is enabled
 */

import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

// ── DEV TOGGLE ────────────────────────────────────────────────────
// Set VITE_PPCA_DOWNLOAD=false in .env to disable PDF download
const DOWNLOAD_ENABLED = import.meta.env.VITE_PPCA_DOWNLOAD !== "false";

// ── API SCHEMA (what Django returns) ─────────────────────────────
/*
GET /api/ppca/verify/:certId/
→ 200 OK:
{
  "cert_id":          "PLU-ABC123-XYZ99",
  "name":             "Jane Smith",
  "state":            "California",
  "state_abbr":       "CA",
  "score":            87,
  "date":             "June 26, 2026",
  "course":           "PPCA Pre-Exam Training",
  "modules":          14,
  "hours":            "6.5",
  "is_special_state": true,
  "verified":         true,
  "expired":          false,
  "issued_at":        "2026-06-26T10:30:00Z",
  "expires_at":       "2027-06-26T10:30:00Z",
  "driver_profile":   "/drivers/jane-smith-PLU",  // optional
}
→ 404 { "error": "Certificate not found" }
→ 410 { "error": "Certificate revoked", "reason": "..." }
*/

// ── MOCK DATA (remove in production — replace with API call) ──────
const MOCK_CERT = {
  cert_id:          "PLU-ABC123-XYZ99",
  name:             "Jane Smith",
  state:            "California",
  state_abbr:       "CA",
  score:            87,
  date:             "June 26, 2026",
  course:           "PPCA Pre-Exam Training",
  modules:          14,
  hours:            "6.5",
  is_special_state: true,
  verified:         true,
  expired:          false,
  issued_at:        "2026-06-26T10:30:00Z",
  expires_at:       "2027-06-26T10:30:00Z",
  driver_profile:   null,
};

// ── STYLES ────────────────────────────────────────────────────────
const S = {
  page: {
    fontFamily: "'Inter', system-ui, sans-serif",
    minHeight: "100vh",
    background: "#F0F4F8",
    color: "#0B1F3A",
    display: "flex",
    flexDirection: "column",
  },
  topbar: {
    background: "#0B1F3A",
    borderBottom: "2px solid #C9A84C",
    padding: "12px 24px",
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  logo: {
    width: 34, height: 34,
    background: "#C9A84C",
    borderRadius: 8,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontWeight: 800, fontSize: 16, color: "#0B1F3A",
  },
  logoTitle: { color: "#fff", fontSize: 15, fontWeight: 700 },
  logoSub:   { color: "#C9A84C", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" },
  content: {
    flex: 1,
    padding: "32px 24px",
    maxWidth: 680,
    margin: "0 auto",
    width: "100%",
  },
  card: {
    background: "#fff",
    borderRadius: 16,
    padding: "24px 28px",
    boxShadow: "0 2px 12px rgba(11,31,58,.08)",
    marginBottom: 16,
  },
  pill: (c, bg, border) => ({
    display: "inline-flex", alignItems: "center", gap: 5,
    background: bg, color: c,
    border: `1px solid ${border}`,
    fontSize: 11, fontWeight: 700,
    padding: "4px 12px", borderRadius: 99,
    letterSpacing: "0.04em",
  }),
  stat: (accent) => ({
    background: "#F8FAFC",
    borderRadius: 10,
    padding: "12px 16px",
    borderLeft: `3px solid ${accent}`,
  }),
  statNum:   { fontSize: 22, fontWeight: 800, color: "#0B1F3A", lineHeight: 1 },
  statLabel: { fontSize: 10, color: "#6B7A8D", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 2 },
  btn: (variant) => ({
    padding: "10px 20px",
    borderRadius: 9,
    fontSize: 13,
    fontWeight: 700,
    border: "none",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontFamily: "inherit",
    background: variant === "primary" ? "#0B1F3A"
               : variant === "gold"    ? "#C9A84C"
               : "#fff",
    color:      variant === "primary" ? "#C9A84C"
               : variant === "gold"    ? "#0B1F3A"
               : "#0B1F3A",
    border:     variant === "outline"  ? "1.5px solid #E2E8F0" : "none",
  }),
  certBox: {
    background: "#fff",
    border: "1.5px solid #C9A84C",
    borderRadius: 14,
    padding: "32px 28px",
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
    marginBottom: 16,
  },
  seal: {
    width: 72, height: 72,
    borderRadius: 99,
    background: "#0B1F3A",
    border: "3px solid #C9A84C",
    display: "flex", alignItems: "center", justifyContent: "center",
    margin: "0 auto 14px",
    fontSize: 28,
  },
  verifiedBanner: {
    background: "linear-gradient(135deg,#0B1F3A,#132B50)",
    borderRadius: 12,
    padding: "16px 20px",
    display: "flex",
    alignItems: "center",
    gap: 14,
    marginBottom: 16,
  },
  verifiedIcon: {
    width: 48, height: 48,
    borderRadius: 99,
    background: "#C9A84C",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 22, flexShrink: 0,
  },
  divider: { border: "none", borderTop: "1px solid #E2E8F0", margin: "14px 0" },
  alert: (variant) => ({
    background: variant === "red" ? "#FEF2F2" : variant === "green" ? "#ECFDF5" : "#FFF9EC",
    border: `1.5px solid ${variant === "red" ? "#fca5a5" : variant === "green" ? "#6ee7b7" : "#C9A84C"}`,
    borderRadius: 10,
    padding: "12px 16px",
    display: "flex", gap: 10, alignItems: "flex-start",
    fontSize: 12, color: "#0B1F3A",
    marginBottom: 14,
  }),
  footer: {
    background: "#0B1F3A",
    borderTop: "1px solid #C9A84C44",
    padding: "16px 24px",
    textAlign: "center",
    fontSize: 11,
    color: "#6B7A8D",
  },
};

// ── LOADING STATE ────────────────────────────────────────────────
function LoadingState() {
  return (
    <div style={{ textAlign: "center", padding: "60px 20px" }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: "#0B1F3A" }}>Verifying certificate...</div>
      <div style={{ fontSize: 12, color: "#6B7A8D", marginTop: 6 }}>Checking PilotLinkUSA records</div>
    </div>
  );
}

// ── NOT FOUND STATE ──────────────────────────────────────────────
function NotFoundState({ certId }) {
  return (
    <div style={{ textAlign: "center", padding: "60px 20px" }}>
      <div style={{ fontSize: 40, marginBottom: 14 }}>❌</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: "#DC2626", marginBottom: 8 }}>Certificate not found</div>
      <div style={{ fontSize: 13, color: "#6B7A8D", marginBottom: 16, maxWidth: 420, margin: "0 auto" }}>
        The certificate ID <strong style={{ fontFamily: "monospace", color: "#0B1F3A" }}>{certId}</strong> does not exist in our records or may have been revoked.
      </div>
      <div style={S.alert("red")}>
        <span style={{ fontSize: 18 }}>⚠️</span>
        <div>If you received this certificate from a driver, it may be fraudulent. Contact PilotLinkUSA to report: <a href="mailto:verify@pilotlinkusa.org" style={{ color: "#DC2626" }}>verify@pilotlinkusa.org</a></div>
      </div>
    </div>
  );
}

// ── REVOKED STATE ────────────────────────────────────────────────
function RevokedState({ certId, reason }) {
  return (
    <div style={{ textAlign: "center", padding: "60px 20px" }}>
      <div style={{ fontSize: 40, marginBottom: 14 }}>🚫</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: "#DC2626", marginBottom: 8 }}>Certificate revoked</div>
      <div style={S.alert("red")}>
        <span style={{ fontSize: 18 }}>⚠️</span>
        <div>Certificate <strong style={{ fontFamily: "monospace" }}>{certId}</strong> has been revoked. {reason && <span>Reason: {reason}</span>}</div>
      </div>
    </div>
  );
}

// ── VERIFIED STATE ───────────────────────────────────────────────
function VerifiedState({ cert, certId, onDownload, downloadEnabled }) {
  const [copied, setCopied] = useState(false);
  const verifyUrl = `${window.location.origin}/verify/${certId}`;
  const scoreColor = cert.score >= 75 ? "#059669" : "#EA580C";
  const isExpired = cert.expired;

  const copyUrl = () => {
    navigator.clipboard.writeText(verifyUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <>
      {/* Verified banner */}
      <div style={S.verifiedBanner}>
        <div style={S.verifiedIcon}>✅</div>
        <div style={{ flex: 1 }}>
          <div style={{ color: "#fff", fontSize: 16, fontWeight: 800 }}>
            {isExpired ? "Certificate expired" : "Certificate verified"}
          </div>
          <div style={{ color: "#C9A84C", fontSize: 11, marginTop: 3 }}>
            {isExpired
              ? "This certificate has expired and is no longer valid."
              : "This is an authentic PilotLinkUSA PPCA training certificate."}
          </div>
        </div>
        <span style={S.pill(
          isExpired ? "#DC2626" : "#059669",
          isExpired ? "#FEF2F2" : "#ECFDF5",
          isExpired ? "#fca5a5" : "#6ee7b7"
        )}>
          {isExpired ? "⚠ Expired" : "✓ Valid"}
        </span>
      </div>

      {/* Certificate */}
      <div style={S.certBox}>
        {/* Decorative pattern */}
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(201,168,76,0.03) 10px, rgba(201,168,76,0.03) 11px)",
          pointerEvents: "none",
        }} />

        <div style={S.seal}>🚗</div>

        <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: "#6B7A8D", marginBottom: 6 }}>
          Certificate of Completion
        </div>
        <div style={{ fontSize: 12, color: "#6B7A8D", marginBottom: 14 }}>
          PPCA Pre-Exam Training Program · PilotLinkUSA
        </div>

        <div style={{ fontSize: 11, color: "#6B7A8D", marginBottom: 5 }}>This certifies that</div>
        <div style={{ fontSize: 26, fontWeight: 800, color: "#0B1F3A", marginBottom: 4 }}>
          {cert.name}
        </div>

        <div style={{ margin: "12px auto", width: 60, height: 2, background: "#C9A84C", borderRadius: 99 }} />

        <div style={{ fontSize: 12, color: "#6B7A8D", lineHeight: 1.7, maxWidth: 480, margin: "0 auto 16px" }}>
          has successfully completed the PPCA Pre-Exam Training Course,
          including FHWA-aligned safety modules, MUTCD flagging fundamentals,
          and {cert.is_special_state ? "specialized" : "standard"} exam
          preparation for the state of <strong style={{ color: "#0B1F3A" }}>{cert.state}</strong>.
          {cert.score && ` Final assessment score: `}
          {cert.score && <strong style={{ color: scoreColor }}>{cert.score}%</strong>}
          {cert.score && "."}
        </div>

        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 16 }}>
          {[
            { label: "Final score", val: `${cert.score}%`, accent: scoreColor },
            { label: "State",       val: cert.state_abbr || cert.state.slice(0,2), accent: "#C9A84C" },
            { label: "Modules",     val: cert.modules || 14, accent: "#3B82F6" },
            { label: "Study hours", val: `${cert.hours || "6.5"}h`, accent: "#8B5CF6" },
          ].map(s => (
            <div key={s.label} style={S.stat(s.accent)}>
              <div style={{ ...S.statNum, color: s.accent }}>{s.val}</div>
              <div style={S.statLabel}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Special state badge */}
        {cert.is_special_state && (
          <div style={{ marginBottom: 14 }}>
            <span style={S.pill("#DC2626","#FEF2F2","#fca5a5")}>
              ⚠ Special cert state — state exam also required
            </span>
          </div>
        )}

        {/* Validity dates */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, fontSize: 11, color: "#6B7A8D", marginBottom: 14 }}>
          <div>Issued: <strong style={{ color: "#0B1F3A" }}>{cert.date}</strong></div>
          <div style={{ fontFamily: "monospace", background: "#F8FAFC", padding: "3px 9px", borderRadius: 4, fontSize: 10 }}>
            {cert.cert_id}
          </div>
          <div>Expires: <strong style={{ color: isExpired ? "#DC2626" : "#0B1F3A" }}>
            {cert.expires_at ? new Date(cert.expires_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "1 year from issue"}
          </strong></div>
        </div>

        {/* Legal disclaimer */}
        <div style={{
          fontSize: 10, color: "#DC2626",
          background: "#FEF2F2", border: "1px solid #fca5a5",
          borderRadius: 7, padding: "8px 12px", lineHeight: 1.6,
        }}>
          ⚠️ This certificate confirms completion of pre-exam training only. It is NOT a state certification and does not grant legal authority to operate as a pilot/escort vehicle operator. The certificate holder must still complete the official {cert.state} state examination and meet all state DOT requirements.
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        {downloadEnabled && !isExpired ? (
          <button style={{ ...S.btn("primary"), flex: 1, justifyContent: "center" }} onClick={onDownload}>
            📄 Download PDF certificate
          </button>
        ) : !downloadEnabled ? (
          <div style={{ flex: 1, padding: "10px 14px", background: "#F8FAFC", borderRadius: 9, fontSize: 12, color: "#6B7A8D", border: "1.5px solid #E2E8F0", textAlign: "center" }}>
            PDF download is disabled for this certificate.
          </div>
        ) : null}
        <button style={{ ...S.btn("outline") }} onClick={copyUrl}>
          {copied ? "✓ Copied!" : "🔗 Copy verify link"}
        </button>
        {cert.driver_profile && (
          <a href={cert.driver_profile} style={{ ...S.btn("gold"), textDecoration: "none" }}>
            View driver profile
          </a>
        )}
      </div>

      {/* For companies / dispatchers */}
      <div style={S.card}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#0B1F3A", marginBottom: 10 }}>
          For companies and dispatchers
        </div>
        <div style={{ fontSize: 12, color: "#6B7A8D", lineHeight: 1.7, marginBottom: 12 }}>
          This certificate verifies that <strong style={{ color: "#0B1F3A" }}>{cert.name}</strong> has completed
          the PPCA pre-exam training course for <strong style={{ color: "#0B1F3A" }}>{cert.state}</strong>.
          This means the driver has studied FHWA regulations, MUTCD flagging standards, state-specific rules,
          and passed a {cert.modules || 14}-module assessment.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px,1fr))", gap: 10 }}>
          {[
            { icon: "✅", title: "Training verified", desc: "Course completion confirmed in our system" },
            { icon: "📋", title: "FHWA knowledge", desc: "Federal regulations, MUTCD, equipment standards" },
            { icon: "🗺️", title: "State prepared", desc: `${cert.state}-specific rules and exam preparation` },
            { icon: "⚠️", title: "Exam still required", desc: `Driver must also pass the official ${cert.state} state exam` },
          ].map(item => (
            <div key={item.title} style={{ padding: "10px 12px", background: "#F8FAFC", borderRadius: 8 }}>
              <div style={{ fontSize: 18, marginBottom: 5 }}>{item.icon}</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#0B1F3A" }}>{item.title}</div>
              <div style={{ fontSize: 11, color: "#6B7A8D", marginTop: 2, lineHeight: 1.5 }}>{item.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Verify URL share */}
      <div style={{ ...S.card, background: "#0B1F3A" }}>
        <div style={{ color: "#C9A84C", fontSize: 12, fontWeight: 700, marginBottom: 8 }}>
          Share this verification link
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{
            flex: 1, background: "rgba(255,255,255,0.08)",
            borderRadius: 7, padding: "9px 12px",
            fontFamily: "monospace", fontSize: 11, color: "#C9A84C",
            wordBreak: "break-all",
          }}>
            {verifyUrl}
          </div>
          <button style={{ ...S.btn("gold"), padding: "9px 14px", flexShrink: 0 }} onClick={copyUrl}>
            {copied ? "✓" : "Copy"}
          </button>
        </div>
        <div style={{ fontSize: 10, color: "#6B7A8D", marginTop: 8 }}>
          Anyone with this link can verify this certificate — no login required.
        </div>
      </div>
    </>
  );
}

// ── MAIN COMPONENT ───────────────────────────────────────────────
export default function TestCertVerifyPage() {
  const { certId } = useParams();
  const [state, setState] = useState("loading");  // loading | found | not_found | revoked
  const [cert, setCert] = useState(null);
  const [revokeReason, setRevokeReason] = useState(null);

  useEffect(() => {
    if (!certId) { setState("not_found"); return; }
    setState("loading");

    // ── Replace this block with real API call in production ──────
    // fetch(`/api/ppca/verify/${certId}/`)
    //   .then(r => {
    //     if (r.status === 404) { setState("not_found"); return null; }
    //     if (r.status === 410) { return r.json().then(d => { setState("revoked"); setRevokeReason(d.reason); return null; }); }
    //     return r.json();
    //   })
    //   .then(data => { if (data) { setCert(data); setState("found"); } })
    //   .catch(() => setState("not_found"));

    // MOCK — remove in production
    setTimeout(() => {
      if (certId && certId.startsWith("PLU-")) {
        setCert({ ...MOCK_CERT, cert_id: certId });
        setState("found");
      } else {
        setState("not_found");
      }
    }, 1200);
    // ── End mock ─────────────────────────────────────────────────
  }, [certId]);

  const handleDownload = async () => {
    if (!DOWNLOAD_ENABLED) return;
    // In production: fetch the PDF from Django
    // const r = await fetch(`/api/ppca/certificate/download/${certId}/`);
    // const blob = await r.blob();
    // const url = URL.createObjectURL(blob);
    // const a = document.createElement("a");
    // a.href = url; a.download = `PPCA_${cert.state_abbr}_${certId}.pdf`;
    // a.click(); URL.revokeObjectURL(url);
    alert("PDF download will be enabled after connecting to the Django backend.");
  };

  return (
    <div style={S.page}>
      {/* Top bar */}
      <div style={S.topbar}>
        <div style={S.logo}>P</div>
        <div>
          <div style={S.logoTitle}>PilotLinkUSA</div>
          <div style={S.logoSub}>Certificate Verification</div>
        </div>
        <div style={{ marginLeft: "auto", color: "#C9A84C", fontSize: 10, fontWeight: 600, background: "rgba(201,168,76,0.1)", border: "1px solid rgba(201,168,76,0.3)", padding: "3px 10px", borderRadius: 99 }}>
          Public Verification Portal
        </div>
      </div>

      {/* Content */}
      <div style={S.content}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#0B1F3A" }}>Certificate Verification</div>
          <div style={{ fontSize: 12, color: "#6B7A8D", marginTop: 3 }}>
            Verifying: <span style={{ fontFamily: "monospace", color: "#0B1F3A" }}>{certId}</span>
          </div>
        </div>

        {state === "loading"   && <LoadingState />}
        {state === "not_found" && <NotFoundState certId={certId} />}
        {state === "revoked"   && <RevokedState certId={certId} reason={revokeReason} />}
        {state === "found"     && (
          <VerifiedState
            cert={cert}
            certId={certId}
            onDownload={handleDownload}
            downloadEnabled={DOWNLOAD_ENABLED}
          />
        )}
      </div>

      {/* Footer */}
      <div style={S.footer}>
        <div style={{ marginBottom: 4 }}>
          <strong style={{ color: "#C9A84C" }}>PilotLinkUSA</strong>
          {" · "}Certificate verification is available 24/7 at no charge.
          {" · "}Questions: <a href="mailto:verify@pilotlinkusa.org" style={{ color: "#C9A84C" }}>verify@pilotlinkusa.org</a>
        </div>
        <div style={{ color: "#4B5563" }}>
          This verification page does not constitute state certification.
          Drivers must also hold a valid state DOT escort permit.
        </div>
      </div>
    </div>
  );
}

// ── DJANGO URL CONFIG ─────────────────────────────────────────────
/*
# urls.py
from django.urls import path
from . import views

urlpatterns = [
    # Public verify — no auth required
    path('verify/<str:cert_id>/',
         views.verify_certificate_public, name='verify_cert'),

    # Download — login required + DOWNLOAD_ENABLED check
    path('api/ppca/certificate/download/<str:cert_id>/',
         views.download_certificate, name='download_cert'),

    # API for the React verify page
    path('api/ppca/verify/<str:cert_id>/',
         views.api_verify_certificate, name='api_verify_cert'),
]
*/

// ── DJANGO VIEWS ──────────────────────────────────────────────────
/*
# views.py

import os
from django.http import HttpResponse, JsonResponse
from django.views.decorators.http import require_GET
from django.views.decorators.csrf import csrf_exempt
from django.utils.decorators import method_decorator

DOWNLOAD_ENABLED = os.getenv('PPCA_DOWNLOAD', 'true').lower() == 'true'


@require_GET
def api_verify_certificate(request, cert_id):
    # Public endpoint — no auth required
    # Rate limit: 60 requests per minute per IP
    try:
        cert = PPCACertificate.objects.select_related('user').get(cert_id=cert_id)
    except PPCACertificate.DoesNotExist:
        return JsonResponse({'error': 'Certificate not found'}, status=404)

    if cert.is_revoked:
        return JsonResponse({
            'error': 'Certificate revoked',
            'reason': cert.revoke_reason or 'Contact PilotLinkUSA for details.'
        }, status=410)

    return JsonResponse({
        'cert_id':          cert.cert_id,
        'name':             cert.user.get_full_name(),
        'state':            cert.state_name,
        'state_abbr':       cert.state_abbr,
        'score':            cert.final_score,
        'date':             cert.completed_at.strftime('%B %d, %Y'),
        'course':           'PPCA Pre-Exam Training',
        'modules':          14,
        'hours':            '6.5',
        'is_special_state': cert.is_special_state,
        'verified':         cert.is_verified,
        'expired':          cert.is_expired,
        'issued_at':        cert.completed_at.isoformat(),
        'expires_at':       cert.expires_at.isoformat() if cert.expires_at else None,
        'driver_profile':   f'/drivers/{cert.user.username}/' if cert.show_profile else None,
    })


@require_GET
def download_certificate(request, cert_id):
    if not request.user.is_authenticated:
        return JsonResponse({'error': 'Authentication required'}, status=401)

    if not DOWNLOAD_ENABLED:
        return JsonResponse({
            'error': 'PDF download is currently disabled.',
            'reason': 'Course content is available for on-platform study only.',
            'view_url': f'/verify/{cert_id}/',
        }, status=403)

    try:
        cert = PPCACertificate.objects.get(
            cert_id=cert_id, user=request.user
        )
    except PPCACertificate.DoesNotExist:
        return JsonResponse({'error': 'Certificate not found'}, status=404)

    from ppca_certificate import generate_certificate
    pdf = generate_certificate({
        'cert_id':          cert.cert_id,
        'name':             request.user.get_full_name(),
        'state':            cert.state_name,
        'state_abbr':       cert.state_abbr,
        'score':            cert.final_score,
        'date':             cert.completed_at.strftime('%B %d, %Y'),
        'modules':          14,
        'hours':            '6.5',
        'is_special_state': cert.is_special_state,
        'verified':         cert.is_verified,
    })

    response = HttpResponse(pdf, content_type='application/pdf')
    response['Content-Disposition'] = (
        f'attachment; filename="PPCA_{cert.state_abbr}_{cert.cert_id}.pdf"'
    )
    response['Content-Length']       = len(pdf)
    response['X-Content-Type-Options'] = 'nosniff'
    response['Cache-Control']        = 'no-store, no-cache, must-revalidate'
    return response
*/
